import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { request } from 'Features/auth/axios-client'
import { getToken } from 'Features/auth/auth'
import {
  CreateSheduleParams,
  DeleteProductionRequirementsParams,
  SchedulerTableDataType,
  UpdateProductionRequirementsParams,
} from './Scheduler.types'
import { PLAN_STATUS_FETCH_TIMEOUT } from 'Common/Constants/Constants'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import type { RunFailure } from './RunFailureDetails'

const fetchShedules = async () => {
  const data = await request({
    url: `/scheduler`,
  })
  return data
}
export const useSheduler = () => {
  return useQuery(['schedules'], () => fetchShedules(), {
    select: ({ data }) => data?.data,
    // Every visit fetches the list afresh, whatever another page that shares
    // this cache key thinks about staleness: the fetch is also what runs
    // SchedulerService's sweep of stopped runs.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: PLAN_STATUS_FETCH_TIMEOUT,
  })
}

/** Who the caller is and what they may do — `GET /me`.
 *
 *  There was no endpoint for this before the roles work: `/user-tenants`
 *  returns the tenants a user can reach, never their role within one.
 *
 *  `permissions` is the union across the user's roles, which is the same test
 *  `security.auth.requires` applies server-side. Both read one `config.ROLES`
 *  mapping, so the UI and the guard cannot disagree about what a role means. */
export type CurrentUser = {
  username: string
  userTitle: string
  tenant: string | null
  roles: string[]
  permissions: string[]
  /** False: licensed on the tenant but no `scheduler` in Planner_Roles, so
   *  every other route answers 403. Absent from an older service. */
  schedulerAccess?: boolean
  /** Their scheduler role (`Scheduler_Roles`, or the default). */
  schedulerRole?: string | null
}

const fetchCurrentUser = async () => {
  const data = await request({ url: `/me` })
  return data
}

export const useCurrentUser = () => {
  return useQuery(['currentUser'], () => fetchCurrentUser(), {
    select: ({ data }) => (data?.data ?? null) as CurrentUser | null,
    // Roles change when an administrator edits a table this app cannot write
    // to, so there is nothing in-session that can invalidate this. Refetching
    // on focus is how a user picks up a role change without a hard reload.
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
    // Retries, where this used to be `retry: false`. That single word is the
    // most likely cause of four rounds of vanishing buttons: `usePermissions`
    // treats an errored /me as "unknown", every permission gate reads
    // unknown, and with no retry one failure stuck for the whole page view
    // until a focus event or remount.
    //
    // /me is the query most exposed to that. `request()` awaits `getToken()`
    // and assigns Authorization onto shared axios defaults, so a /me firing
    // before MSAL has settled gets a 401 — once, transiently, and then never
    // asks again. Two retries with backoff covers it without hammering a
    // genuinely down service.
    retry: 2,
  })
}

/** What the UI should offer.
 *
 *  **This decides what to SHOW, never what is ALLOWED.** Every mutating route
 *  is guarded server-side; this only stops the Portal offering a control that
 *  would come back 403.
 *
 *  `isLoading` matters more than it looks. The permissions list is empty both
 *  for a genuine view-only user AND for a request that carried no `Tenant`
 *  header — which is every request before a tenant has been selected, because
 *  `aad_auth` skips the licence checks that attach roles. Treating "not yet
 *  known" as "denied" would flash a read-only UI at every user on load, so
 *  callers should gate on `canWrite`/`canRun` only once `isLoading` is false,
 *  which is what the defaults below already do. */
export type TenantUser = {
  email: string
  /** The effective scheduler role — what the user can actually do. */
  role: string
  /** `assigned`: chosen in `Scheduler_Roles`. `default`: the cell is blank.
   *  `invalid`: it holds an unrecognised value. Both of the last two resolve
   *  to the default role server-side. */
  roleSource: 'assigned' | 'default' | 'invalid'
  /** What is literally stored, for the `invalid` case. */
  storedRole: string
  licenceAllocated: boolean
  /** Whether the CALLER may change this user's role (an Admin: everyone; a
   *  Super User: Users and Viewers only). Absent from an older service. */
  editable?: boolean
}

export type RoleOption = { value: string; label: string }

/** Every user on this tenant with the planner's `scheduler` role, for the
 *  Manage users screen. Admin-only: `GET /users` carries the `manage_users`
 *  permission, so a caller without it gets a 403 rather than an empty list —
 *  which is why the screen is also hidden behind `canManageUsers`. */
export const useTenantUsers = () =>
  useQuery(
    ['tenant-users'],
    async () => {
      const { data } = await request({ url: '/users' })
      return data as {
        users: TenantUser[]
        /** What the caller may set: all four for an Admin, User and Viewer
         *  for a Super User. */
        assignableRoles: RoleOption[]
        /** Every role, so a row the caller cannot change still shows its
         *  role. Absent from an older service. */
        allRoles?: RoleOption[]
        defaultRole: string
        canAssign: boolean
      }
    },
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  )

/** Set one user's scheduler role (`PUT /users/{email}/role`). Writes only
 *  `Scheduler_Roles` on their profile row. */
export const useSetUserRole = (
  onSuccess: () => void,
  onError: (message: string) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(
    async (vars: { email: string; role: string }) =>
      request({
        method: 'put',
        url: `/users/${encodeURIComponent(vars.email)}/role`,
        data: { role: vars.role },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tenant-users'])
        // The admin may have changed their own role.
        queryClient.invalidateQueries(['currentUser'])
        onSuccess()
      },
      onError: (e: any) =>
        onError(e?.response?.data?.error ?? 'Failed to change the role'),
    }
  )
}

export type PublishedSchedule = {
  id: string
  schedulerId: string
  scheduleName: string
  iteration: string
  variantKey: string
  weekNumber: number
  /** ISO date of the Monday this published week covers. The business key: the
   *  deck warns on "Schedule for W/C DD.MM.YYYY already exists", and two
   *  different schedules can cover the same calendar week. */
  weekCommencing: string
  publishedBy: string
  publishedAt: string
  /** False once a later publish covered the same week. Superseded rows are
   *  still returned — dropping them is how a planner concludes the feature
   *  ate their work — so the screen greys them out on this. */
  isCurrent: boolean
}

/** Every published week across every schedule, newest first. Also the Viewer's
 *  entire world, which is why the route behind it is not permission-gated. */
export const usePublishedSchedules = () =>
  useQuery(
    ['published-schedules'],
    async () => {
      const { data } = await request({ url: '/published-schedules' })
      return (data?.published ?? []) as PublishedSchedule[]
    },
    { staleTime: 60000 }
  )

export const usePublishSchedule = (
  onSuccess: () => void,
  onError: (e: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(
    async (vars: {
      schedulerId: string
      iteration: string
      variantKey: string
      weeks: number[]
    }) =>
      request({
        method: 'post',
        url: `/scheduler/${vars.schedulerId}/publish`,
        data: {
          iteration: vars.iteration,
          variantKey: vars.variantKey,
          weeks: vars.weeks,
        },
      }),
    {
      onSuccess: () => {
        // The list is what every warning in the publish dialog is computed
        // from, so it has to be refetched before the dialog could be reopened.
        queryClient.invalidateQueries(['published-schedules'])
        onSuccess()
      },
      onError,
    }
  )
}

export const usePermissions = () => {
  const { data, isLoading, isError } = useCurrentUser()
  const permissions = data?.permissions ?? []

  /** True only when the server has actually answered. */
  const known = !isLoading && !isError && data != null

  /**
   * **Every gate below asks "has the server said no?", never "has it said
   * yes?"** — so a control disappears only on an explicit denial, and an
   * unreachable, slow or restarting service leaves the UI as it was.
   *
   * This used to split the flags two ways: `canWrite`/`canRun` defaulted
   * permissive while the four Super User gates defaulted to FALSE, on the
   * reasoning that hiding a control you should not see is the safe way to be
   * briefly wrong. That reasoning was wrong, and it cost real time: it
   * collapses "I cannot tell" and "you are not allowed" into one invisible
   * button, so a service that was merely restarting was indistinguishable
   * from a role restriction — with nothing on screen to tell them apart.
   *
   * Hiding is not a security control in any case. Every one of these is
   * enforced server-side: `requires_write`, `requires(publish)`,
   * `requires(system_of_record)`, `requires(configuration_settings)`,
   * `requires(manage_users)` in SchedulerService, and `run` on the Rust API's
   * run routes. The worst case of being optimistic here is a 403 that says
   * why; the worst case of being pessimistic is what you have just lived
   * through.
   */
  const allowed = (permission: string) =>
    known ? permissions.includes(permission) : true

  return {
    isLoading,
    /** False until /me has answered — the one flag callers can use to tell
     *  "not yet known" from a real answer, rather than inferring it. */
    known,
    roles: data?.roles ?? [],
    canWrite: allowed('write'),
    canRun: allowed('run'),
    canManageUsers: allowed('manage_users'),
    /** Can open Manage Users: an Admin, or a Super User (who may then only
     *  move people between User and Viewer — the server decides per row). */
    canViewUsers: allowed('view_users') || allowed('manage_users'),
    canPublish: allowed('publish'),
    canManageSor: allowed('system_of_record'),
    canManageConfig: allowed('configuration_settings'),
    // Read-only is the one that must NOT be optimistic: it turns editing off,
    // so assuming it while the answer is unknown would lock a legitimate
    // editor out of their own tables — the same mistake in the other
    // direction. Unknown means "not view-only".
    isViewOnly: known ? !permissions.includes('write') : false,
  }
}

/**
 * One schedule's display name, for the breadcrumb.
 *
 * There is no single-schedule endpoint — `GET /scheduler` (the list) is the
 * only place the name lives — so this shares the list's query key and selects
 * a single row out of it, reusing the cache whenever the list page has already
 * loaded it.
 *
 * Deliberately without `useSheduler`'s `refetchInterval`: that poll exists to
 * keep run status fresh on the list page, and a breadcrumb label has no reason
 * to re-fetch every few seconds. React Query keeps these options per observer,
 * so mounting this adds no polling of its own.
 *
 * Returns '' until the list resolves, so a caller can fall back to a static
 * label rather than flashing an empty crumb.
 */
export const useScheduleName = (schedulerId: string) => {
  return useQuery(['schedules'], () => fetchShedules(), {
    enabled: Boolean(schedulerId),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    select: ({ data }) =>
      ((data?.data ?? []) as SchedulerTableDataType[]).find(
        (s) => String(s.id) === String(schedulerId)
      )?.name ?? '',
  })
}

const createSchedule = async (schedulerData: CreateSheduleParams) => {
  const { data } = await request({
    method: 'post',
    data: schedulerData,
    url: `/scheduler`,
  })
  return data
}

export const useCreateScheule = (
  /** `created` is the new schedule's id and first version, when the service
   *  returns them (it does from 2026-10-06; an older one sends neither). */
  onSuccess: (created?: { id: string; iteration: string }) => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(createSchedule, {
    onSuccess: (response: any) => {
      // `createSchedule` already returns the body: { message, data: { id, … } }.
      const body = response?.data
      onSuccess(
        body?.id
          ? { id: String(body.id), iteration: String(body.iteration ?? '0') }
          : undefined
      )
      queryClient.invalidateQueries('schedules')
    },
    onError: () => {
      onFailure()
    },
  })
}

const fetchScheduleWorkstationDowntime = async (sheduleId: string) => {
  const data = await request({
    url: `/scheduler/${sheduleId}/workstation-downtime`,
  })
  return data
}
export const useScheduleWorkstationDowntime = (sheduleId: string) => {
  return useQuery(
    ['schedule-workstation-downtime', sheduleId],
    () => fetchScheduleWorkstationDowntime(sheduleId),
    {
      select: ({ data }) => data?.data,
      refetchOnWindowFocus: false,
    }
  )
}

/**
 * The holding-tank closing-state REPORT for one week — engine OUTPUT, not an
 * input table, so it comes off `/schedulerView/...` like the baskets endpoint
 * and never through the input-table CRUD.
 *
 * It is what the planner copies into next week's opening quantity: the
 * cross-week handoff is manual in V1 by design, so nothing reads this back
 * automatically. A schedule with no tank, or one that has not been run, gets
 * an empty list and a 200 rather than an error.
 */
const fetchTankClosingState = async (
  scheduleId: string,
  iteration: string,
  week: number
) => {
  const data = await request({
    url: `/schedulerView/${scheduleId}/${iteration}/tank-state?week=${week}`,
  })
  return data
}

export const useTankClosingState = (
  scheduleId: string,
  iteration: string,
  week: number,
  enabled = true
) => {
  return useQuery(
    ['tank-closing-state', scheduleId, iteration, week],
    () => fetchTankClosingState(scheduleId, iteration, week),
    {
      select: ({ data }) => data?.data ?? [],
      refetchOnWindowFocus: false,
      // The report describes the LAST run. A stale one would show a planner
      // numbers from a superseded schedule and invite them to type those into
      // next week, so it is never cached across a re-run.
      staleTime: 0,
      enabled,
    }
  )
}

const fetchScheduleProductionRequirements = async (
  sheduleId: string,
  API: string,
  iteration = '0',
  week?: number
) => {
  const params = week !== undefined ? `?week=${week}` : ''
  const data = await request({
    url: `/scheduler/${sheduleId}/${API}/${iteration}${params}`,
  })
  return data
}

export const useScheduleProductionRequirements = (
  sheduleId: string,
  API: string,
  iteration = '0',
  week?: number,
  enabled = true
) => {
  const queryClient = useQueryClient()

  const query = useQuery(
    [API, sheduleId, iteration, week],
    () => fetchScheduleProductionRequirements(sheduleId, API, iteration, week),
    {
      select: ({ data }) => data,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      // A tab you come back to within a minute reuses what it already had.
      // This was 0, which means "always stale": every remount of every input
      // tab re-read its table, so switching away and back was always a round
      // trip even though nothing could have changed. Writes invalidate this
      // key explicitly, so a stale window costs nothing on your own edits.
      staleTime: 60000,
      enabled,
    }
  )

  // Prefetch the adjacent weeks so the week buttons switch instantly.
  //
  // GATED ON `enabled`. It was not, and that quietly defeated the only thing
  // protecting this route: `useAllWeekRequirements` mounts eight of these
  // hooks (weeks 1-8) disabled by default, precisely so the master-data
  // screen does not pay for eight reads — but each disabled hook still ran
  // this effect and prefetched its two neighbours, so opening the tab fired
  // all eight anyway. The gate said off; the network said otherwise.
  //
  // `staleTime` here must match the query's. `prefetchQuery` with 0 ALWAYS
  // goes to the network, cache hit or not, so the "prefetch" was re-fetching
  // the neighbours on every single week change rather than filling a gap.
  useEffect(() => {
    if (
      enabled &&
      week !== undefined &&
      (API === 'production-requirements' || API === 'workstation-downtime')
    ) {
      const adjacentWeeks = [week - 1, week + 1].filter((w) => w >= 1 && w <= 8)
      adjacentWeeks.forEach((w) => {
        queryClient.prefetchQuery(
          [API, sheduleId, iteration, w],
          () =>
            fetchScheduleProductionRequirements(sheduleId, API, iteration, w),
          { staleTime: 60000 }
        )
      })
    }
  }, [enabled, week, sheduleId, API, iteration, queryClient])

  return query
}

// Write PUT request to update production requirements

const updateProductionRequirements = async ({
  schedulerId,
  data,
  API,
  iteration = '0',
  week,
}: UpdateProductionRequirementsParams<{}> & { week?: number }) => {
  const params = week !== undefined ? `?week=${week}` : ''
  await request({
    method: 'put',
    data,
    url: `/scheduler/${schedulerId}/${API}/${iteration}${params}`,
  })
}

export const useUpdateProductionRequirements = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(updateProductionRequirements, {
    onSuccess: (_, variables) => {
      onSuccess()
      queryClient.invalidateQueries([
        variables.API,
        variables.schedulerId,
        variables.iteration,
      ])
      // Refresh data status immediately so "Edited" badges appear
      queryClient.invalidateQueries([
        'data-status',
        variables.schedulerId,
        variables.iteration,
      ])
    },
    onError: (error) => {
      console.log('error', error)
      onFailure()
    },
  })
}

// POST REQUEST TO  "/scheduler/<scheduler_id>/<directory_mapping>/upload and sending File as form data"

type UploadFileParams = {
  file: File
  schedulerId: string
  API: string
  iteration?: string
  weekNumber?: number
}

const uploadScheduleDataFile = async ({
  file,
  schedulerId,
  API,
  iteration = '0',
  weekNumber,
}: UploadFileParams) => {
  const formData = new FormData()
  formData.append('file', file)
  const params = weekNumber !== undefined ? `?week=${weekNumber}` : ''
  const { data } = await request({
    method: 'post',
    data: formData,
    url: `/scheduler/${schedulerId}/${API}/${iteration}/upload${params}`,
  })
  return data
}

export const useUploadScheduleDataFile = (
  onSuccess: (data?: any) => void,
  onFailure: (error?: any) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(uploadScheduleDataFile, {
    onSuccess: (data, variables) => {
      console.log('data', data)
      queryClient.invalidateQueries([
        variables.API,
        variables.schedulerId,
        variables.iteration,
      ])
      queryClient.invalidateQueries([
        'data-status',
        variables.schedulerId,
        variables.iteration,
      ])
      onSuccess(data.merge_info)
    },
    onError: (error) => {
      console.error('Upload error:', error)
      onFailure(error)
    },
  })
}

const deleteProductionRequirementRows = async ({
  schedulerId,
  rows,
  API,
  iteration = '0',
  week,
}: DeleteProductionRequirementsParams & { week?: number }) => {
  const params = week !== undefined ? `?week=${week}` : ''
  await request({
    method: 'delete',
    data: rows,
    url: `/scheduler/${schedulerId}/${API}/${iteration}${params}`,
  })
}

export const useDeleteProductionRequirements = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(deleteProductionRequirementRows, {
    onSuccess: (_, variables) => {
      onSuccess()
      queryClient.invalidateQueries([
        variables.API,
        variables.schedulerId,
        variables.iteration,
      ])
      queryClient.invalidateQueries([
        'data-status',
        variables.schedulerId,
        variables.iteration,
      ])
    },
    onError: (error) => {
      console.log('error', error)
      onFailure()
    },
  })
}

// Bundles the save/upload/delete mutations most input-table views need into
// the shape SheduleTableProps<T> expects, so each view doesn't re-wire the
// same three mutations + toasts by hand.
export const useSchedulerTableHandlers = <T>(
  schedulerId: string,
  api: string,
  iteration: string,
  week?: number
) => {
  const onUpdateSuccess = () => toast.success('Data updated')
  const onUpdateFailure = () => toast.error('Failed to update data')
  const { mutate: saveData, isLoading: isSaving } =
    useUpdateProductionRequirements(onUpdateSuccess, onUpdateFailure)

  const onDeleteSuccess = () => toast.success('Rows deleted')
  const onDeleteFailure = () => toast.error('Failed to delete rows')
  const { mutateAsync: deleteData, isLoading: isDeleting } =
    useDeleteProductionRequirements(onDeleteSuccess, onDeleteFailure)

  const [updateRowsStatus, setUpdateRowsStatus] = useState({})
  const onUploadSuccess = (mergeInfo: Record<string, unknown>) => {
    setUpdateRowsStatus(mergeInfo)
    toast.success('Data uploaded')
  }
  const onUploadFailure = () => toast.error('Failed to upload data')
  const { mutate: uploadData, isLoading: isUploading } =
    useUploadScheduleDataFile(onUploadSuccess, onUploadFailure)

  const updateTableData = useCallback(
    (changedRows: Record<number, T>) => {
      saveData({
        data: changedRows as Record<number, {}>,
        schedulerId,
        API: api,
        iteration,
        week,
      })
    },
    [saveData, schedulerId, api, iteration, week]
  )

  const uploadScheduleData = useCallback(
    (file: File) => {
      uploadData({ file, schedulerId, API: api, iteration, weekNumber: week })
    },
    [uploadData, schedulerId, api, iteration, week]
  )

  const deleteTableData = useCallback(
    (rows: T[]) =>
      deleteData({
        rows: rows as unknown as Record<string, unknown>[],
        schedulerId,
        API: api,
        iteration,
        // Per-week tables (trials, production requirements, downtime) live
        // at Week{N}/…; without this the delete targeted the BASE folder,
        // found nothing, and the service's `_try_delete_rows` reported
        // `rows_deleted: 0` as success — so the row stayed and the UI said
        // "Rows deleted". `updateTableData` and the upload have always sent
        // it; only this one did not.
        week,
      }),
    [deleteData, schedulerId, api, iteration, week]
  )

  return {
    updateTableData,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  }
}

// Post request to  "/scheduler/<scheduler_id>/run-scheduler"

type RunSchedulerParams = {
  schedulerId: string
  iteration?: string
  config?: SchedulerRunConfig
  /**
   * `/prepare-data` only: the API starts the run itself when the survey
   * ends. The page used to submit the run from the survey's completion
   * callback, so leaving the page during the survey meant it never ran.
   */
  thenRun?: boolean
}

export type SchedulerRunConfig = {
  pop_size?: number
  generations?: number
  early_stop?: number
  // NSGA-II PRNG seed. Different seeds explore different regions of the
  // Pareto front for the same inputs. Default 42 server-side if absent.
  seed?: number
  allow_cleaning_during_downtime?: boolean
  // Option D — when on, the decoder schedules additional production
  // fragments into capped-resource-free windows that the main pass missed.
  // Default false; opt-in per run.
  enable_resource_yield?: boolean
  // Solver strictness (rule settings — shape the search)
  w_changeover_cost?: number
  w_changeover_env_cost?: number
  // Hours of makespan the solver will give up to avoid one CIP. 0 = off.
  //
  // Clean count is ALREADY minimised without this: it is NSGA-II objective
  // F2 (`total_clean_count + changeover_count`), so the search always
  // prefers fewer cleans among schedules that are otherwise equal. This
  // weight is the separate question of whether cleans should beat a shorter
  // schedule, and it is applied inside F1 alongside makespan (engine
  // v7.35.0+). Raise in small steps.
  w_clean_count?: number
  w_crew_constraint?: number
  w_minrun?: number
  w_late?: number
  // Business priority weights (rank the Pareto set afterward)
  w_user_throughput?: number
  w_user_service?: number
  w_user_ops?: number
  w_user_balance?: number
  // Mid-week re-plan
  fix_before_hour?: number
  not_produced_keys?: string[]
  // Hard floor on when ANY product can start (see RunConfig::min_start_hour
  // in scheduler_rust). Needed alongside fix_before_hour/not_produced_keys:
  // those only stop already-fixed rows from being re-placed — freeing up
  // everything before the cutoff removes every downtime block that would
  // otherwise apply, leaving the solver free to backdate fresh production
  // before the cutoff too. Set to the same hour as fix_before_hour.
  min_start_hour?: number
  // Mid-week reschedule "Least disruption" toggle: among the fastest
  // schedules, the engine publishes the one whose re-planned products start
  // closest to the original. Rust `RunConfig::midweek_prefer_closest`,
  // default false.
  midweek_prefer_closest?: boolean
  // Per-batch product ordering preference. Maps to the Rust
  // `BatchProductOrder` enum.
  // - `Auto`: GA decides via the evolved chromosome (default)
  // - `SmallestFirst`: ascending volume — smallest demand first
  // - `LargestFirst`: descending volume — largest demand first
  batch_product_order?: 'Auto' | 'SmallestFirst' | 'LargestFirst'
  // Strictness toggles for missing changeover rules. Default true (free
  // transition). Set false to drop products whose transitions have no
  // defined rule in the corresponding SOR table.
  allow_missing_base_material_changeover?: boolean
  allow_missing_product_changeover?: boolean
  // Advanced GA knobs — exploration vs exploitation tuning. Optional;
  // server-side defaults track NSGAII2Config::default():
  //   mutation_prob=0.4, crossover_prob=0.9,
  //   mutation_rate_adaptivity=0.5, tournament_size=3.
  // Raising mutation_prob and lowering tournament_size both widen
  // exploration; raising mutation_rate_adaptivity strengthens the
  // "explore early, exploit late" schedule.
  mutation_prob?: number
  crossover_prob?: number
  mutation_rate_adaptivity?: number
  tournament_size?: number
  // Small-remainder demand adjustment ("small run removal"). At a kitchen
  // max-run boundary the decoder may DROP a leftover rather than open a
  // fresh batch and pay its CIP clean storm. Both gates must pass: the
  // remainder must be under `small_batch_drop_hours` at the best eligible
  // line rate AND under `small_batch_drop_max_fraction` of the original run.
  //
  // These gates are load-bearing. Dropped quantity is credited against
  // demand, so the engine reports it as MET, not unmet, and no feasibility
  // gate sees it — widening either value silently deletes demand. Measured
  // on 31697730656 with the gates wide open, the engine deleted ~18% of the
  // week's demand while still reporting unmet=0 and validation OK. The
  // demand-adjustments panel (ScheduleDemandAdjustments.view) is the only
  // place that shows what actually fired. Set hours to 0 to disable.
  //
  // Absent ⇒ engine defaults (2.0 / 0.10) — see scheduler_rust types.rs.
  small_batch_drop_hours?: number
  small_batch_drop_max_fraction?: number
  // Survey breadth: how many full-depth probes "Prepare Data" runs before Run
  // refines the winner. The survey is where most of the final makespan is
  // decided (it picks the structural basin), so more probes can find a better
  // schedule, at roughly proportional extra Prepare time. Absent ⇒ the
  // engine default, 60. 0 ⇒ no survey at all: Run refines straight from the
  // feasibility check's best schedule (sent as `skip_survey`, API 0.4.11).
  survey_probe_count?: number
  // Override the scheduler's time horizon (hours). Used by multi-week
  // consolidated runs to pass (N * 168 + tail_buffer_hours). Absent ⇒
  // server default 168.
  time_horizon?: number
  // Sorted list of week numbers to consolidate into a single solve. When
  // present and ≥ 2, scheduler_api merges these weeks' production-requirements
  // into one demand list, runs a single NSGA-II solve with the provided
  // time_horizon, then slices the output rows back into per-week Deltas.
  // Unselected weeks are skipped entirely. Absent / empty ⇒ legacy per-week
  // loop behaviour.
  consolidated_weeks?: number[]
}

// ─── Scheduler Settings snapshot (read-only) ───────────────────────────────
//
// Persisted by `scheduler_api` at run time to SchedulerSettingsDelta. Read by
// the Portal to show "settings used for this run" on the schedule results
// page. Fields mirror the Rust RunConfig struct exactly, plus three
// metadata fields appended by the writer.
//
// Shape returned by `GET /api/v1/scheduler/{id}/settings/{iter}`:
//   { data: SchedulerSettingsSnapshot | null, exists: boolean }
export type SchedulerSettingsSnapshot = {
  // Metadata (written by scheduler_api, not part of RunConfig)
  scheduler_id: string
  iteration: string
  run_timestamp_unix: number
  run_status: 'running' | 'complete' | 'failed' | string
  job_id?: string | null
  // Genetic algorithm
  pop_size: number
  generations: number
  early_stop: number
  time_horizon: number
  seed: number
  // Rule strictness
  allow_cleaning_during_downtime: boolean
  enable_resource_yield: boolean
  w_changeover_cost: number
  w_changeover_env_cost: number
  w_global_makespan: number
  w_line_makespan: number
  w_schedule_end: number
  w_crew_constraint: number
  w_minrun: number
  w_late: number
  // Business priorities
  w_user_throughput: number
  w_user_service: number
  w_user_ops: number
  w_user_balance: number
  // Mid-week re-plan + batch order
  fix_before_hour?: number | null
  not_produced_keys?: string | null // JSON-encoded array
  min_start_hour?: number | null
  batch_product_order: string
  // Strictness toggles for missing changeover rules
  allow_missing_base_material_changeover: boolean
  allow_missing_product_changeover: boolean
  // Advanced GA knobs (mirrors RunConfig)
  mutation_prob: number
  crossover_prob: number
  mutation_rate_adaptivity: number
  tournament_size: number
  // Multi-week consolidated metadata. JSON-encoded `[1,2,3]` of weeks the
  // user selected for the consolidated solve, or `null` for a single-week
  // / legacy per-week run. The Portal parses this on load to restore the
  // multi-week UI state (e.g. the multi-week CSV button) even after a
  // browser reload that drops the ephemeral useState in ScheduleControlPanel.
  consolidated_weeks?: string | null
  // Small-remainder demand adjustment gates. Nullable: the snapshot only
  // gained these columns after the fact, so a row written by an older API
  // reads back as null. That must mean "not recorded" and be omitted, never
  // 0 — 0 hours DISABLES the rule, so coercing a missing value to 0 would
  // silently turn small-run dropping off. See `settingsToRunConfig`.
  small_batch_drop_hours?: number | null
  small_batch_drop_max_fraction?: number | null
  // Survey probe count. Null on older snapshots and when the run used the
  // engine default — both mean 60. 0 = a refine-only run (no survey).
  survey_probe_count?: number | null
}

const fetchScheduleSettings = async (
  schedulerId: string,
  iteration: string
) => {
  const { data } = await request({
    url: `/scheduler/${schedulerId}/settings/${iteration}`,
  })
  return data as { data: SchedulerSettingsSnapshot | null; exists: boolean }
}

/**
 * Read-only fetch of the scheduler settings snapshot for a given iteration.
 * Returns `{ data: null, exists: false }` if no schedule has been run yet.
 *
 * Invalidates whenever the scheduler is re-run (via the same query key
 * pattern as other per-iteration endpoints); call sites that trigger a
 * run should invalidate `['scheduler-settings', schedulerId, iteration]`.
 */
export const useScheduleSettings = (schedulerId: string, iteration: string) => {
  return useQuery(
    ['scheduler-settings', schedulerId, iteration],
    () => fetchScheduleSettings(schedulerId, iteration),
    {
      refetchOnWindowFocus: false,
      // No retry on 404-like cases — the absent-table case is a 200 with
      // exists=false, so a missed retry doesn't hide useful errors.
      retry: 1,
    }
  )
}

/**
 * Parse the snapshot's `consolidated_weeks` column into a sorted week list,
 * or `undefined` for anything that isn't a genuine multi-week consolidated
 * run.
 *
 * `undefined` is the single most consequential value on a schedule screen:
 * it decides whether the rows on disk carry ABSOLUTE consolidated-horizon
 * hours (week 2 starting at 168) or plain per-week 0–168 ones, and so
 * whether every view has to subtract `(week - earliest) * 168` first.
 *
 * A single-element list is deliberately NOT consolidated: the input page
 * sends `consolidated_weeks: [n]` for an ordinary single-week run just to
 * make the API skip the other weeks in its per-week loop, and that path
 * writes week-relative times.
 */
export const parseConsolidatedWeeks = (
  raw: string | null | undefined
): number[] | undefined => {
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed) && parsed.every((n) => typeof n === 'number')) {
      const sorted = (parsed as number[]).slice().sort((a, b) => a - b)
      return sorted.length >= 2 ? sorted : undefined
    }
  } catch {
    // Malformed snapshot — fall back to single-week behaviour.
  }
  return undefined
}

/**
 * The weeks consolidated into this iteration's last run, or `undefined` for
 * a single-week / legacy per-week run. Wraps `useScheduleSettings` +
 * `parseConsolidatedWeeks` so every view derives the multi-week offset from
 * one implementation.
 */
export const useConsolidatedWeeks = (
  schedulerId: string,
  iteration: string
): number[] | undefined => {
  const { data } = useScheduleSettings(schedulerId, iteration)
  const raw = data?.data?.consolidated_weeks
  return useMemo(() => parseConsolidatedWeeks(raw), [raw])
}

// ─── Tenant Configuration Settings (source of truth for run defaults) ──────
//
// Persisted in ConfigurationSettingsDelta (tenant-scoped, one row). Read by
// the Configuration Settings dialog when opened from the Scheduler page, and
// by ScheduleControlPanel as its lowest-priority fallback. Per-run edits in
// the control panel do NOT write back here — only the dialog's Save & Exit
// button does, and only when the dialog is opened *without* a schedulerId
// (i.e. from the tenant-wide Scheduler page, not from inside a plan — see
// usePlanConfigurationSettings/useUpdatePlanConfigurationSettings below for
// the per-plan variant the dialog uses instead when opened as "Configure
// run" from inside a specific plan).
export type ConfigurationSettings = {
  // Genetic algorithm
  pop_size: number
  generations: number
  early_stop: number
  seed: number
  mutation_prob: number
  crossover_prob: number
  mutation_rate_adaptivity: number
  tournament_size: number
  // Feature toggles
  allow_cleaning_during_downtime: boolean
  enable_resource_yield: boolean
  allow_missing_base_material_changeover: boolean
  allow_missing_product_changeover: boolean
  // Rule strictness (solver weights)
  w_changeover_cost: number
  w_changeover_env_cost: number
  // See SchedulerRunConfig.w_clean_count. Not carried by
  // SchedulerSettingsSnapshot — the Delta schema has no column for it.
  w_clean_count: number
  w_crew_constraint: number
  w_minrun: number
  w_late: number
  // Business priorities (user weights)
  w_user_throughput: number
  w_user_service: number
  w_user_ops: number
  w_user_balance: number
  // Batch ordering
  batch_product_order: 'Auto' | 'SmallestFirst' | 'LargestFirst'
  // Tail buffer (hours) appended to a multi-week consolidated horizon so
  // production that started late in the last week has room to finish.
  // time_horizon = (selectedWeeks * 168) + tail_buffer_hours.
  tail_buffer_hours: number
  // Small-remainder demand adjustment gates — see SchedulerRunConfig for
  // what they do and why they must not be widened casually.
  small_batch_drop_hours: number
  small_batch_drop_max_fraction: number
  // Survey breadth — see SchedulerRunConfig.survey_probe_count.
  survey_probe_count: number
}

// Maps a persisted settings row (either the tenant SOR ConfigurationSettings
// or a per-iteration SchedulerSettingsSnapshot — both share these field
// names exactly) onto the GA/weight fields of SchedulerRunConfig. Used by
// any run trigger that doesn't have its own GA-settings UI, so it preserves
// whatever was last used instead of silently resetting to server defaults.
// Deliberately excludes time_horizon/consolidated_weeks (multi-week-only).
/** `{ [key]: value }` when the settings row carries a usable number there,
 *  `{}` otherwise — so a missing or null column falls through to the engine's
 *  own serde default rather than being forwarded as null or coerced to 0. */
const numeric = <K extends string>(
  key: K,
  s: ConfigurationSettings | SchedulerSettingsSnapshot
): Partial<Record<K, number>> => {
  const v = (s as Record<string, unknown>)[key]
  return typeof v === 'number' && Number.isFinite(v)
    ? ({ [key]: v } as Record<K, number>)
    : {}
}

const isBatchProductOrder = (
  v: unknown
): v is 'Auto' | 'SmallestFirst' | 'LargestFirst' =>
  v === 'Auto' || v === 'SmallestFirst' || v === 'LargestFirst'

export const settingsToRunConfig = (
  s: ConfigurationSettings | SchedulerSettingsSnapshot
): SchedulerRunConfig => ({
  pop_size: s.pop_size,
  generations: s.generations,
  early_stop: s.early_stop,
  seed: s.seed,
  mutation_prob: s.mutation_prob,
  crossover_prob: s.crossover_prob,
  mutation_rate_adaptivity: s.mutation_rate_adaptivity,
  tournament_size: s.tournament_size,
  allow_cleaning_during_downtime: s.allow_cleaning_during_downtime,
  enable_resource_yield: s.enable_resource_yield,
  allow_missing_base_material_changeover:
    s.allow_missing_base_material_changeover,
  allow_missing_product_changeover: s.allow_missing_product_changeover,
  w_changeover_cost: s.w_changeover_cost,
  w_changeover_env_cost: s.w_changeover_env_cost,
  w_crew_constraint: s.w_crew_constraint,
  w_minrun: s.w_minrun,
  w_late: s.w_late,
  w_user_throughput: s.w_user_throughput,
  w_user_service: s.w_user_service,
  w_user_ops: s.w_user_ops,
  w_user_balance: s.w_user_balance,
  // The small-batch gates are carried by ConfigurationSettings AND, since the
  // snapshot schema was widened, by SchedulerSettingsSnapshot too — which is
  // what stops a re-run of an already-run plan silently reverting them to the
  // engine defaults.
  //
  // `in` alone is NOT enough now that the snapshot declares the columns: a row
  // written by an older API has the field present and null, and `in` would
  // forward that null into RunConfig, where `small_batch_drop_hours: f64`
  // cannot deserialise it — the whole run config would fail to parse. Test the
  // VALUE, so an unrecorded gate is omitted and the engine's serde default
  // applies, which is the behaviour those rows always had.
  ...numeric('small_batch_drop_hours', s),
  ...numeric('small_batch_drop_max_fraction', s),
  ...numeric('survey_probe_count', s),
  ...('w_clean_count' in s ? { w_clean_count: s.w_clean_count } : {}),
  // A snapshot stores the engine enum's Debug name as a plain string, and a
  // row from before the column existed has none; anything but the three
  // names the engine accepts is omitted so its serde default (Auto) applies.
  ...(isBatchProductOrder(s.batch_product_order)
    ? { batch_product_order: s.batch_product_order }
    : {}),
})

// ─── Mid-week re-plan: shared not-produced key expansion ───────────────────
//
// A "kitchen batch" (one base-material cook) feeds one or more "line
// products" (packing runs). Marking either as not-produced should imply the
// other: marking the kitchen batch frees every line product that depends on
// it, and marking every one of a kitchen batch's line products implies the
// kitchen batch itself never ran. Key formats are a contract with the Rust
// scheduler's not_produced_keys matching — do not change them independently
// of scheduler_api.
export const kitchenBatchKey = (row: InputRow): string =>
  `${row.base_material || row.productCode}_${row.kitchen}_${row.kitchen_start_time}`
export const lineProductKey = (row: InputRow): string =>
  `${row.productCode}_${row.line}_${row.line_start_time}`

export const buildKitchenLineKeyMaps = (outputRows: InputRow[]) => {
  const kitchenToLines = new Map<string, string[]>()
  const lineToKitchen = new Map<string, string>()
  for (const row of outputRows) {
    const kitchenKey = kitchenBatchKey(row)
    const lineKey = lineProductKey(row)
    if (!kitchenToLines.has(kitchenKey)) kitchenToLines.set(kitchenKey, [])
    kitchenToLines.get(kitchenKey)!.push(lineKey)
    lineToKitchen.set(lineKey, kitchenKey)
  }
  return { kitchenToLines, lineToKitchen }
}

export const expandNotProducedKeys = (
  notProducedIds: Set<string>,
  outputRows: InputRow[]
): Set<string> => {
  if (notProducedIds.size === 0 || outputRows.length === 0)
    return new Set(notProducedIds)

  const expanded = new Set(notProducedIds)
  const { kitchenToLines } = buildKitchenLineKeyMaps(outputRows)

  // Kitchen batch marked not-produced → every dependent line product too.
  for (const [kitchenKey, lineKeys] of kitchenToLines) {
    if (notProducedIds.has(kitchenKey)) {
      for (const lk of lineKeys) expanded.add(lk)
    }
  }
  // Every one of a kitchen batch's line products marked → the kitchen batch too.
  for (const [kitchenKey, lineKeys] of kitchenToLines) {
    if (
      lineKeys.length > 0 &&
      lineKeys.every((lk) => notProducedIds.has(lk) || expanded.has(lk))
    ) {
      expanded.add(kitchenKey)
    }
  }
  return expanded
}

const fetchConfigurationSettings = async () => {
  const data = await request({ url: `/scheduler/configuration-settings` })
  return data
}

export const useConfigurationSettings = () => {
  return useQuery(
    ['configuration-settings'],
    () => fetchConfigurationSettings(),
    {
      select: ({ data }) => data?.data as ConfigurationSettings | undefined,
      refetchOnWindowFocus: false,
    }
  )
}

const updateConfigurationSettings = async (settings: ConfigurationSettings) => {
  const { data } = await request({
    method: 'put',
    data: settings,
    url: `/scheduler/configuration-settings`,
  })
  return data
}

export const useUpdateConfigurationSettings = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(updateConfigurationSettings, {
    onSuccess: () => {
      queryClient.invalidateQueries(['configuration-settings'])
      onSuccess()
    },
    onError: () => onFailure(),
  })
}

// ─── Per-plan Configuration Settings override ──────────────────────────────
//
// Persisted in a ConfigurationSettingsDelta scoped to one scheduler_id +
// iteration (sibling of the tenant-wide table above, and of the read-only
// per-iteration run-settings snapshot `useScheduleSettings` reads). Written
// only when a user saves "Configure run" from inside a specific plan — never
// touches the tenant-wide settings. `isPlanOverride` distinguishes "this
// plan has its own saved settings" from "no override yet, showing the
// tenant SOR as a fallback" so callers (ScheduleControlPanel's seed-priority
// logic) can tell the two apart.
export type PlanConfigurationSettingsResult = {
  data: ConfigurationSettings
  isPlanOverride: boolean
}

const fetchPlanConfigurationSettings = async (
  schedulerId: string,
  iteration: string
) => {
  const data = await request({
    url: `/scheduler/${schedulerId}/configuration-settings/${iteration}`,
  })
  return data
}

export const usePlanConfigurationSettings = (
  schedulerId: string,
  iteration: string,
  enabled = true
) => {
  return useQuery(
    ['plan-configuration-settings', schedulerId, iteration],
    () => fetchPlanConfigurationSettings(schedulerId, iteration),
    {
      select: ({ data }) => data as PlanConfigurationSettingsResult | undefined,
      refetchOnWindowFocus: false,
      enabled: enabled && !!schedulerId,
    }
  )
}

type UpdatePlanConfigurationSettingsParams = {
  schedulerId: string
  iteration: string
  settings: ConfigurationSettings
}

const updatePlanConfigurationSettings = async ({
  schedulerId,
  iteration,
  settings,
}: UpdatePlanConfigurationSettingsParams) => {
  const { data } = await request({
    method: 'put',
    data: settings,
    url: `/scheduler/${schedulerId}/configuration-settings/${iteration}`,
  })
  return data
}

export const useUpdatePlanConfigurationSettings = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(updatePlanConfigurationSettings, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        'plan-configuration-settings',
        variables.schedulerId,
        variables.iteration,
      ])
      onSuccess()
    },
    onError: () => onFailure(),
  })
}

// ─── Multi-week "Don't start before" stagger ───────────────────────────────
//
// When the user toggles multi-week mode off in the schedule control panel,
// the system rewrites the dontStartBefore column on each selected week's
// production-requirements rows: weekToHour maps the week number to the new
// dontStartBefore value (in hours from the start of the consolidated
// horizon). For each week we fetch the current rows, set dontStartBefore on
// every row, and PUT the batch back so the merge predicate updates them
// in-place. The column remains editable in the per-week table afterwards.
//
// (Earlier attempt also wrote `finishBefore = dontStartBefore + 168` to
// enable the Rust soft late-penalty for per-week placement preference.
// That write zeroed out weeks 2 and 3 in production — root cause not yet
// diagnosed; suspect type-coercion through the pandas/Delta merge path.
// Reverted to single-column write until the corruption mode is understood.)
type ApplyDontStartBeforeStaggerParams = {
  schedulerId: string
  iteration: string
  weekToHour: Record<number, number | null>
}

const applyDontStartBeforeStagger = async ({
  schedulerId,
  iteration,
  weekToHour,
}: ApplyDontStartBeforeStaggerParams) => {
  for (const [weekStr, hour] of Object.entries(weekToHour)) {
    const week = Number(weekStr)
    const fetchResult = await request({
      url: `/scheduler/${schedulerId}/production-requirements/${iteration}?week=${week}`,
    })
    const rows = (fetchResult?.data?.data ?? []) as Array<
      Record<string, unknown>
    >
    if (rows.length === 0) continue
    const updated: Record<number, Record<string, unknown>> = {}
    rows.forEach((row, idx) => {
      updated[idx] = { ...row, dontStartBefore: hour }
    })
    await request({
      method: 'put',
      data: updated,
      url: `/scheduler/${schedulerId}/production-requirements/${iteration}?week=${week}`,
    })
  }
}

export const useApplyDontStartBeforeStagger = (
  onSuccess: (weeksApplied: number) => void,
  onError: (err: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(applyDontStartBeforeStagger, {
    onSuccess: (_, variables) => {
      // Invalidate the per-week production-requirements queries and the
      // data-status badge so the UI reflects the new dontStartBefore values
      // without a manual refresh.
      queryClient.invalidateQueries([
        'production-requirements',
        variables.schedulerId,
        variables.iteration,
      ])
      queryClient.invalidateQueries([
        'data-status',
        variables.schedulerId,
        variables.iteration,
      ])
      onSuccess(Object.keys(variables.weekToHour).length)
    },
    onError: (err) => onError(err),
  })
}

// ─── Resource groups: one value for every week ─────────────────────────────
//
// A resource's group (`resourceGroupCode`, v7.180.0) lives on the workstation
// downtime table, which is stored per week, but it is part of the resource's
// identity and packaging routes (shared by every week) name groups. So a group
// edit on one week is written to the same resource in every other week and in
// the base table the weeks are seeded from. Each week's rows are read and
// written back whole, changing only the group: the service's merge replaces a
// matched row with what it is sent, so a partial row would blank that week's
// downtime. Reading a week the service has no folder for creates it as a copy
// of the base table, which is the data that week already used.
type ApplyResourceGroupsParams = {
  schedulerId: string
  iteration: string
  /** resource -> group code; null clears it (the resource is its own group). */
  groups: Record<string, string | null>
  /** The week the edit was saved on — already written, so skipped here. */
  skipWeek?: number
}

const RESOURCE_GROUP_WEEKS = [1, 2, 3, 4, 5, 6, 7, 8]

const applyResourceGroups = async ({
  schedulerId,
  iteration,
  groups,
  skipWeek,
}: ApplyResourceGroupsParams) => {
  const targets: (number | undefined)[] = [
    undefined,
    ...RESOURCE_GROUP_WEEKS.filter((w) => w !== skipWeek),
  ]
  for (const week of targets) {
    const params = week !== undefined ? `?week=${week}` : ''
    const url = `/scheduler/${schedulerId}/workstation-downtime/${iteration}${params}`
    const fetchResult = await request({ url })
    const rows = (fetchResult?.data?.data ?? []) as Array<
      Record<string, unknown>
    >
    const updated: Record<number, Record<string, unknown>> = {}
    rows.forEach((row, idx) => {
      const resource = String(row.resource ?? '')
      if (!(resource in groups)) return
      const next = groups[resource]
      const current = row.resourceGroupCode ?? null
      if ((current || null) === (next || null)) return
      updated[idx] = { ...row, resourceGroupCode: next }
    })
    if (Object.keys(updated).length === 0) continue
    await request({ method: 'put', data: updated, url })
  }
}

export const useApplyResourceGroups = () => {
  const queryClient = useQueryClient()
  return useMutation(applyResourceGroups, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        'workstation-downtime',
        variables.schedulerId,
        variables.iteration,
      ])
    },
    onError: () => {
      toast.error('Failed to copy the resource group to every week')
    },
  })
}

// ─── Scheduler Progress (SSE from Rust API) ────────────────────────────────

/**
 * Where a run is, coarsely — what the progress banner names. Set by the Rust
 * API (`scheduler_api/src/progress.rs`), the same whether the run executes in
 * the API or in a Container Apps job.
 */
export type RunPhase =
  | 'queued'
  | 'starting'
  | 'loading'
  | 'preparing'
  | 'optimising'
  | 'saving'
  | 'completed'
  | 'failed'
  | 'cancelled'

/** One week's state as the API reports it. A week not listed has not started. */
export type WeekProgress = {
  week: number
  status: 'running' | 'complete' | 'failed'
  makespan?: number
  unmet_demand?: number
  rows?: number
}

export type SchedulerProgress = {
  stage: string
  message: string
  generation?: number
  total_generations?: number
  elapsed_ms?: number
  current_week?: number
  total_weeks?: number
  week_makespan?: number
  week_unmet_demand?: number
  week_rows?: number
  /**
   * The fields below come from an API with the progress hub (0.4.1+): every
   * event carries the whole state, so the latest one alone draws the panel.
   * Optional so an older API's events still work through the per-event fold.
   */
  phase?: RunPhase
  weeks?: WeekProgress[]
  /** When the run last showed signs of life, on the API's clock. */
  updated_at_ms?: number
  /** True on the first event of a stream: the state so far. */
  snapshot?: boolean
}

const TERMINAL_PHASES: RunPhase[] = ['completed', 'failed', 'cancelled']

/** True once a progress event says the job is over. */
export const isFinishedProgress = (p: SchedulerProgress) =>
  p.stage === 'completed' ||
  p.stage === 'failed' ||
  (!!p.phase && TERMINAL_PHASES.includes(p.phase))

const weeksToResults = (weeks: WeekProgress[]) =>
  weeks.reduce<Record<number, WeekResult>>((acc, w) => {
    acc[w.week] = {
      week: w.week,
      makespan: w.makespan ?? 0,
      unmet_demand: w.unmet_demand ?? 0,
      rows: w.rows ?? 0,
      status: w.status,
    }
    return acc
  }, {})

// Replaced at build time by webpack DefinePlugin.
const SCHEDULER_BASE_URL = process.env.SCHEDULER_BASE_URL as string | undefined

/** Per-week result accumulated from SSE events. */
export type WeekResult = {
  week: number
  makespan: number
  unmet_demand: number
  rows: number
  status: 'pending' | 'running' | 'complete' | 'failed'
}

/**
 * One small-remainder demand adjustment made by the decoder
 * (`RunConfig::small_batch_drop_hours` — production default 2h, plus the
 * 10% `small_batch_drop_max_fraction` gate).
 *
 * These quantities are DELETED from the plan but credited against demand, so
 * the engine reports `unmet_demand: 0` for them. They still show up in the
 * demand-fulfilment views as a shortfall (those derive `demand - produced`
 * from the schedule rows), which is why surfacing this list matters: without
 * it a planner cannot tell a deliberate policy drop from a genuine capacity
 * failure, and the two need completely different responses.
 *
 * Mirrors `scheduler::types::DemandAdjustment` / the Rust API's
 * `SchedulerResult.demand_adjustments` (omitted from the payload when empty).
 */
export type DemandAdjustment = {
  product_id: string
  base_material: string
  kitchen: string
  /** Units dropped. */
  quantity: number
  /** What those units would have taken at the best eligible line rate. */
  hours_equivalent: number
  /** Human-readable explanation built by the decoder, including both gates. */
  reason: string
}

/** The subset of the Rust API's `SchedulerResult` the Portal reads back. */
export type SchedulerNoticeSeverity = 'error' | 'warning' | 'info'

/**
 * One planner-facing line from preprocessing — a trial rejected, a carry-over
 * refused, demand dropped for want of a route.
 *
 * NOT the same thing as the engine's `warnings` field, which is a verbose
 * preprocessing trace (145 push sites: parse counts, interner maps, params
 * summaries) and is useless in a UI. The engine distils it in
 * `scheduler::notices`; this is the distilled half. Don't switch this to
 * `warnings` "for completeness" — that was the state that made every rejection
 * invisible, and rendering the raw trace is what made warnings unshowable in
 * the first place.
 */
export type SchedulerNotice = {
  severity: SchedulerNoticeSeverity
  message: string
}

export type SchedulerJobResult = {
  makespan?: number
  unmet_demand?: number
  demand_adjustments?: DemandAdjustment[]
  notices?: SchedulerNotice[]
}

/**
 * Hook that manages SSE progress subscription for a scheduler job.
 * Tracks per-week status for the week overview UI.
 *
 * Every event from an API with the progress hub carries the run's whole
 * state (phase, every week so far), and the stream opens with a snapshot, so
 * the newest event alone draws the panel: a late connection, a reconnect or a
 * missed event can no longer leave it blank or a week tile wrong. `apply` is
 * also fed the same state by the job-status poll, the backstop when the
 * stream is down; whichever is newer (`updated_at_ms`) wins.
 *
 * A dropped stream reconnects with backoff (1s, 2s, 4s … 15s) for as long as
 * the job is being watched. It used to close for good on the first error,
 * which left the bar frozen for the rest of the run.
 */
export const useSchedulerProgress = () => {
  const [progress, setProgress] = useState<SchedulerProgress | null>(null)
  const [weekResults, setWeekResults] = useState<Record<number, WeekResult>>({})
  const [jobId, setJobId] = useState<string | null>(null)
  const eventSourceRef = useRef<EventSource | null>(null)
  const retryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptsRef = useRef(0)
  /** The job being watched; null once reset or finished (no reconnects). */
  const activeJobRef = useRef<string | null>(null)
  const lastAtRef = useRef(0)

  const closeStream = () => {
    if (retryRef.current) {
      clearTimeout(retryRef.current)
      retryRef.current = null
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
  }

  /** Take one progress state, from the stream or a poll; the newer wins. */
  const apply = useCallback((data: SchedulerProgress) => {
    const at = data.updated_at_ms ?? 0
    if (at && at < lastAtRef.current) return
    if (at) lastAtRef.current = at
    setProgress(data)

    if (data.weeks) {
      // The API's whole picture of the weeks: replace, never merge.
      setWeekResults(weeksToResults(data.weeks))
    } else if (data.current_week) {
      // An older API: fold this one event in.
      setWeekResults((prev) => {
        const updated = { ...prev }
        if (data.stage === 'running') {
          updated[data.current_week!] = {
            week: data.current_week!,
            makespan: 0,
            unmet_demand: 0,
            rows: 0,
            status: 'running',
          }
        } else if (data.stage === 'week_complete') {
          updated[data.current_week!] = {
            week: data.current_week!,
            makespan: data.week_makespan ?? 0,
            unmet_demand: data.week_unmet_demand ?? 0,
            rows: data.week_rows ?? 0,
            status: 'complete',
          }
        } else if (data.stage === 'week_failed') {
          updated[data.current_week!] = {
            week: data.current_week!,
            makespan: 0,
            unmet_demand: 0,
            rows: 0,
            status: 'failed',
          }
        }
        return updated
      })
    }
  }, [])

  const connect = useCallback(
    (id: string) => {
      const es = new EventSource(`${SCHEDULER_BASE_URL}/jobs/${id}/stream`)
      eventSourceRef.current = es

      es.onmessage = (event) => {
        attemptsRef.current = 0
        try {
          const data: SchedulerProgress = JSON.parse(event.data)
          apply(data)
          if (isFinishedProgress(data)) {
            // Over: no more events will come, and none should be asked for.
            activeJobRef.current = null
            closeStream()
          }
        } catch {
          // Ignore parse errors
        }
      }

      es.onerror = () => {
        es.close()
        if (eventSourceRef.current === es) eventSourceRef.current = null
        if (activeJobRef.current !== id) return
        const delay = Math.min(15000, 1000 * 2 ** attemptsRef.current)
        attemptsRef.current += 1
        retryRef.current = setTimeout(() => {
          retryRef.current = null
          if (activeJobRef.current === id) connect(id)
        }, delay)
      }
    },
    [apply]
  )

  const subscribe = useCallback(
    (newJobId: string) => {
      closeStream()
      activeJobRef.current = newJobId
      attemptsRef.current = 0
      lastAtRef.current = 0
      setJobId(newJobId)
      setProgress({
        stage: 'queued',
        phase: 'queued',
        message: 'Job submitted...',
      })
      setWeekResults({})
      connect(newJobId)
    },
    [connect]
  )

  const reset = useCallback(() => {
    activeJobRef.current = null
    closeStream()
    setProgress(null)
    // Keep weekResults so the UI shows completed weeks after the job finishes
    setJobId(null)
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeJobRef.current = null
      closeStream()
    }
  }, [])

  return { progress, weekResults, jobId, subscribe, reset, apply }
}

// ─── Run Scheduler (dual-path: Rust API or Flask fallback) ─────────────────

/**
 * Build the `RunAzureRequest` JSON body shared by `/run-scheduler` and
 * `/prepare-data` — both endpoints take the same shape (schedule_id,
 * iteration, tenant, config, optional consolidated_weeks) and differ only
 * in which phase of the GA the Rust API runs.
 */
const buildRunAzureRequestBody = ({
  schedulerId,
  iteration = '0',
  config,
  thenRun,
}: RunSchedulerParams) => {
  const tenantId = localStorage.getItem('tenantId')
  return {
    schedule_id: schedulerId,
    iteration,
    tenant: tenantId,
    ...(thenRun ? { then_run: true } : {}),
    // Top-level (not inside config) so scheduler_api can branch BEFORE
    // building per-week tables. Omitted when not in consolidated mode.
    ...(config?.consolidated_weeks?.length
      ? { consolidated_weeks: config.consolidated_weeks }
      : {}),
    config: config
      ? {
          pop_size: config.pop_size,
          generations: config.generations,
          early_stop: config.early_stop,
          // Override the 168-hour default when the caller passes an
          // extended horizon (used for multi-week consolidated runs:
          // N * 168 + tail_buffer_hours). Falls back to 168 otherwise.
          // TEMP TESTING OVERRIDE: 840 (5 weeks) to give the GA enough room
          // to fit demand that was dropping at the 168h single-week ceiling.
          // Revert to 168 before pushing.
          time_horizon: config.time_horizon ?? 840,
          // Use the user's seed (UI default 42) instead of pinning to 42
          // here. Different seeds explore different Pareto-front regions.
          seed: config.seed ?? 42,
          allow_cleaning_during_downtime:
            config.allow_cleaning_during_downtime ?? false,
          enable_resource_yield: config.enable_resource_yield ?? false,
          w_changeover_cost: config.w_changeover_cost ?? 0,
          w_changeover_env_cost: config.w_changeover_env_cost ?? 0,
          w_global_makespan: config.w_global_makespan ?? 100,
          w_line_makespan: config.w_line_makespan ?? 1,
          w_crew_constraint: config.w_crew_constraint ?? 1,
          // Previously missing from the POST body — these user-controlled
          // weights were being dropped at the wire boundary, causing the
          // Rust API to fall back to RunConfig defaults (1.0 / 0.25). The
          // settings-snapshot Delta then honestly recorded defaults
          // instead of the values the user set on the slider.
          w_minrun: config.w_minrun ?? 1,
          w_late: config.w_late ?? 1,
          w_user_throughput: config.w_user_throughput ?? 0.25,
          w_user_service: config.w_user_service ?? 0.25,
          w_user_ops: config.w_user_ops ?? 0.25,
          w_user_balance: config.w_user_balance ?? 0.25,
          ...(config.fix_before_hour != null && {
            fix_before_hour: config.fix_before_hour,
          }),
          ...(config.not_produced_keys?.length && {
            not_produced_keys: config.not_produced_keys,
          }),
          ...(config.min_start_hour != null && {
            min_start_hour: config.min_start_hour,
          }),
          ...(config.midweek_prefer_closest && {
            midweek_prefer_closest: true,
          }),
          ...(config.batch_product_order && {
            batch_product_order: config.batch_product_order,
          }),
          // Strictness toggles. Default true on the Rust side too, but
          // we send the exact value the user set so the snapshot
          // records what the run actually used.
          allow_missing_base_material_changeover:
            config.allow_missing_base_material_changeover ?? true,
          allow_missing_product_changeover:
            config.allow_missing_product_changeover ?? true,
          // Advanced GA knobs — explicit values flow into NSGAII2Config
          // and into the settings snapshot. Defaults match the Rust side
          // so omission is identical to passing the defaults explicitly.
          mutation_prob: config.mutation_prob ?? 0.4,
          crossover_prob: config.crossover_prob ?? 0.9,
          mutation_rate_adaptivity: config.mutation_rate_adaptivity ?? 0.5,
          tournament_size: config.tournament_size ?? 3,
          // These four were on SchedulerRunConfig, set by Configure run and
          // the control panel, and dropped HERE: this body is a list, so a
          // field not named below never reaches the API, which then used its
          // own default (a 1-probe survey ran 60 probes). Omitted rather than
          // nulled when unset — the API's fields are numbers, not options.
          // 0 is a real value for the small-batch gates (it disables them),
          // so only null/undefined is left out. A probe count of 0 means
          // "no survey" and goes as `skip_survey`, because the API's 0 is
          // "the engine default" (60).
          ...(config.w_clean_count != null && {
            w_clean_count: config.w_clean_count,
          }),
          ...(config.small_batch_drop_hours != null && {
            small_batch_drop_hours: config.small_batch_drop_hours,
          }),
          ...(config.small_batch_drop_max_fraction != null && {
            small_batch_drop_max_fraction: config.small_batch_drop_max_fraction,
          }),
          ...(config.survey_probe_count != null &&
            config.survey_probe_count > 0 && {
              survey_probe_count: Math.round(config.survey_probe_count),
            }),
          ...(config.survey_probe_count === 0 && { skip_survey: true }),
        }
      : undefined,
  }
}

/**
 * POST a scheduler job to the Rust API (async with SSE progress) — shared
 * by `/run-scheduler` (full survey+refine or refine-only-if-prepared) and
 * `/prepare-data` (survey-only). `endpointPath` picks which.
 */
const postSchedulerJob = async (
  endpointPath: '/run-scheduler' | '/prepare-data',
  params: RunSchedulerParams
): Promise<{ job_id: string; stream_url: string; next_job_id?: string }> => {
  const token = await getToken()
  const tenantId = localStorage.getItem('tenantId')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token?.idToken) {
    headers['Authorization'] = `Bearer ${token.idToken}`
  }
  if (tenantId) {
    headers['Tenant'] = tenantId
  }

  const response = await fetch(`${SCHEDULER_BASE_URL}${endpointPath}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(buildRunAzureRequestBody(params)),
  })

  if (!response.ok) {
    const bodyText = await response.text().catch(() => '')
    let parsed: unknown = undefined
    try {
      parsed = JSON.parse(bodyText)
    } catch {
      /* non-JSON body */
    }
    console.error(`[postSchedulerJob] HTTP error (${endpointPath})`, {
      url: `${SCHEDULER_BASE_URL}${endpointPath}`,
      status: response.status,
      statusText: response.statusText,
      body: parsed ?? bodyText,
    })
    const errorField =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : undefined
    throw new Error(
      errorField ||
        `Scheduler job submission failed (HTTP ${response.status} ${response.statusText})`
    )
  }

  return response.json()
}

const runSchedulerDirect = (params: RunSchedulerParams) =>
  postSchedulerJob('/run-scheduler', params)

/**
 * Submit a "Prepare Data" job — survey-only, persists a chromosome for a
 * later `/run-scheduler` call to refine. Never writes to PlanScheduler's
 * status (see `useDataPrepare`) — preparing data is not a run.
 */
const prepareDataDirect = (params: RunSchedulerParams) =>
  postSchedulerJob('/prepare-data', params)

// The Flask-backed record behind `/plan-scheduler` (table `PlanScheduler`) is
// never updated by the direct-to-Rust run path above — Portal writes it back
// explicitly so the list reflects Running/Success/Failed instead of being
// stuck at whatever status the schedule was created with.
type SchedulerRunStatus = 'RUNNING' | 'SUCCESS' | 'FAILED'

type UpdateSchedulerStatusParams = {
  schedulerId: string
  status: SchedulerRunStatus
  runId?: string
}

const updateSchedulerStatus = async ({
  schedulerId,
  status,
  runId,
}: UpdateSchedulerStatusParams) => {
  await request({
    method: 'put',
    url: `/scheduler/${schedulerId}/status`,
    data: { status, ...(runId ? { runId } : {}) },
  })
}

/**
 * Max time to wait for a "Run" job before considering it timed out. Run's
 * refine phase is a handful of full-cost seeds — normally minutes, not
 * tens of minutes.
 */
const RUN_JOB_TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes

/**
 * Max time to wait for a "Prepare Data" job. Survey runs 30 cheap seeds
 * concurrently, so its wall-clock time is far more sensitive than Run's to
 * how many cores the App Service plan's container actually has (see
 * `cpu_budget.rs`) — a smaller plan can legitimately take much longer even
 * with survey's now-fixed cheap settings (`SURVEY_POP_SIZE`/
 * `SURVEY_GENERATIONS` in `nsga2.rs`). Deliberately longer than Run's
 * timeout rather than sharing one constant, since a real 10-minute Prepare
 * run on a constrained plan previously surfaced as a client-side timeout
 * that looked like a crash when the job was actually still progressing
 * server-side (no server-side timeout exists — `GET /api/v1/jobs/:id` is
 * always the source of truth over how long a job has actually been
 * running).
 */
const PREPARE_JOB_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

type AsyncSchedulerJobCallbacks = {
  /**
   * Fires once the initial POST is accepted (202 + job_id), before polling
   * starts. Optional — `useDataPrepare` omits it, since Prepare must never
   * touch the visible run-status badge (`useRunSchedulerDirect` uses it to
   * mark PlanScheduler RUNNING).
   */
  onSubmitted?: (variables: RunSchedulerParams, jobId: string) => void
  /** `nextJobId`: the run a `thenRun` Prepare handed off to, if any. */
  onSuccess: (
    variables: RunSchedulerParams,
    jobId: string,
    result?: SchedulerJobResult,
    nextJobId?: string
  ) => void
  /**
   * `origin` says who decided the job failed. `'server'`: the Rust API
   * reported it failed. `'client'`: this page gave up watching (timeout,
   * lost connection, refused submit), which says nothing about the run
   * itself, so it must never be recorded as the schedule's status.
   */
  onFailure: (
    errorMessage: string | undefined,
    variables: RunSchedulerParams,
    origin: JobFailureOrigin,
    nextJobId?: string,
    failures?: RunFailure[]
  ) => void
  /** Client-side polling cutoff — `RUN_JOB_TIMEOUT_MS`/`PREPARE_JOB_TIMEOUT_MS`. */
  timeoutMs: number
}

export type JobFailureOrigin = 'server' | 'client'

/**
 * Consecutive 404s (2s apart) before a resumed job is treated as gone.
 */
const MISSING_JOB_POLLS = 8

/**
 * Jobs some mounted hook is already watching. Several run hooks can be
 * mounted on one page (the page's own, the mid-week reschedule's, a control
 * panel's), and a resumed job must be watched by exactly one of them, or its
 * completion fires every one's toasts and callbacks.
 */
const watchedJobs = new Set<string>()

/**
 * Jobs some hook has seen finish (or found gone). The schedule list can still
 * say RUNNING for a moment after that, until its next refetch, and resuming
 * from that stale row would replay the finished job's callbacks.
 */
const settledJobs = new Set<string>()

/**
 * The run each schedule's cached results belong to, as `status|runId` from
 * the schedule list, for this browser session.
 *
 * The schedule's own data (`['scheduler', id, …]`) is cached with
 * `staleTime: Infinity`, and only the page that WATCHED a run to its end
 * refreshed it. A run that ended while the planner was on another schedule
 * (or on the list) left this schedule showing the previous run's results
 * until a full reload, under a status that already said SUCCESS. Every page
 * tracking a schedule now compares the list row with what it last saw, and a
 * run that has ended since refreshes that schedule's data.
 */
const seenRunStamp = new Map<string, string>()

/**
 * Shared async-job plumbing behind both "Run" and "Prepare Data": submit,
 * subscribe to SSE progress, poll `/jobs/:id` every 2s, time out after
 * `timeoutMs`. Extracted from the original `useRunSchedulerDirect` so
 * `/prepare-data` can reuse identical job-lifecycle handling — the two only
 * differ in which endpoint they submit to, how long they're willing to
 * wait, and what happens on submit/success/failure (PlanScheduler status
 * bookkeeping for Run, nothing for Prepare).
 *
 * `resume` attaches the same watcher to a job started elsewhere, for a page
 * opened while its schedule is still running. The job runs on the server
 * either way: leaving the page only ever stopped the WATCHING.
 */
const useAsyncSchedulerJob = (
  submitFn: (
    params: RunSchedulerParams
  ) => Promise<{ job_id: string; stream_url: string; next_job_id?: string }>,
  { onSubmitted, onSuccess, onFailure, timeoutMs }: AsyncSchedulerJobCallbacks
) => {
  const {
    progress,
    weekResults,
    subscribe,
    reset,
    apply: applyProgress,
  } = useSchedulerProgress()
  /**
   * Seconds since the run last showed signs of life, from the job-status
   * poll (measured on the API's clock, so a browser clock cannot skew it).
   * Null while nothing is watched or the API is too old to say.
   */
  const [lastUpdateSecs, setLastUpdateSecs] = useState<number | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const watchedJobRef = useRef<string | null>(null)
  const [isJobRunning, setIsJobRunning] = useState(false)
  const [runResult, setRunResult] = useState<SchedulerJobResult | null>(null)

  const release = () => {
    if (watchedJobRef.current) {
      watchedJobs.delete(watchedJobRef.current)
      watchedJobRef.current = null
    }
  }

  // Cleanup polling and timeout on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      release()
    }
  }, [])

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    release()
    setIsJobRunning(false)
    setLastUpdateSecs(null)
    reset()
  }, [reset])

  /** Watch `jobId` to completion. `resumed`: the job was started elsewhere. */
  const watch = (
    jobId: string,
    variables: RunSchedulerParams,
    resumed: boolean
  ) => {
    watchedJobs.add(jobId)
    watchedJobRef.current = jobId
    setIsJobRunning(true)
    // Clear the previous run's adjustments so a fresh run never shows
    // stale drops alongside new week results.
    setRunResult(null)
    // Subscribe to SSE progress stream
    subscribe(jobId)

    // Overall timeout. Only this page stops watching: the run carries on,
    // and the server records how it ends.
    timeoutRef.current = setTimeout(() => {
      cleanup()
      onFailure(
        `Still running after ${Math.round(timeoutMs / 60000)} minutes. ` +
          'It carries on on the server; the schedule list shows the result when it finishes.',
        variables,
        'client'
      )
    }, timeoutMs)

    // Poll for completion
    let failCount = 0
    let missingCount = 0
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${SCHEDULER_BASE_URL}/jobs/${jobId}`)
        if (!res.ok) {
          // A resumed job the API does not know. Briefly expected: a run a
          // survey hands off to exists only once the survey has released
          // the iteration. For longer, it is gone (the API restarted, or
          // the job finished long enough ago to be evicted): stop quietly,
          // and let the schedule list carry the outcome the server recorded.
          if (resumed && res.status === 404) {
            missingCount++
            if (missingCount >= MISSING_JOB_POLLS) {
              settledJobs.add(jobId)
              cleanup()
            }
            return
          }
          failCount++
          if (failCount >= 10) {
            cleanup()
            onFailure('Lost connection to scheduler API', variables, 'client')
          }
          return
        }
        failCount = 0
        missingCount = 0
        const job = await res.json()
        const nextJobId: string | undefined = job.next_job_id ?? undefined
        // The same state the stream carries: keeps the panel right while the
        // stream is down or reconnecting.
        if (job.progress) applyProgress(job.progress as SchedulerProgress)
        setLastUpdateSecs(
          typeof job.last_update_secs === 'number' ? job.last_update_secs : null
        )
        // A resumed watcher of a survey that hands off to its run follows
        // the run, quietly: to whoever opened the page, it is one run. The
        // page that started the survey gets `nextJobId` in its callbacks
        // instead, and hands it to its run hook.
        if (
          resumed &&
          nextJobId &&
          (job.status === 'completed' || job.status === 'failed') &&
          !settledJobs.has(nextJobId)
        ) {
          settledJobs.add(jobId)
          cleanup()
          watch(nextJobId, variables, true)
          return
        }
        if (job.status === 'completed') {
          settledJobs.add(jobId)
          cleanup()
          // `job.result` is the Rust API's SchedulerResult. It carries
          // `demand_adjustments` — the audit trail for quantity the
          // small-remainder rule deleted — which is only available here,
          // on the run response. It is not persisted to the schedule
          // record, so it cannot be recovered once this job is polled
          // away; hold it for the run-summary UI.
          const result: SchedulerJobResult | undefined = job.result
          setRunResult(result ?? null)
          onSuccess(variables, jobId, result, nextJobId)
        } else if (job.status === 'failed') {
          settledJobs.add(jobId)
          cleanup()
          console.error('[useAsyncSchedulerJob] job failed', {
            job_id: jobId,
            error: job.error,
            message: job.message,
            diagnostics: job.result?.diagnostics,
            result: job.result,
            full: job,
          })
          try {
            console.error(
              '[useAsyncSchedulerJob] job failed (full JSON)',
              JSON.stringify(job, null, 2)
            )
          } catch {
            /* circular */
          }
          // Extract error message from the last progress event if available
          const errorMsg =
            job.error ||
            job.message ||
            job.result?.diagnostics?.find(
              (d: string) => d.includes('FAILED') || d.includes('failed')
            )
          onFailure(
            errorMsg || 'Scheduler job failed',
            variables,
            'server',
            nextJobId,
            Array.isArray(job.failures) ? job.failures : undefined
          )
        }
      } catch {
        failCount++
        if (failCount >= 10) {
          cleanup()
          onFailure('Lost connection to scheduler API', variables, 'client')
        }
      }
    }, 2000)
  }

  const mutation = useMutation(submitFn, {
    onSuccess: (data, variables) => {
      onSubmitted?.(variables, data.job_id)
      watch(data.job_id, variables, false)
    },
    onError: (error, variables) => {
      cleanup()
      console.error('[useAsyncSchedulerJob] submit failed', error)
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to submit scheduler job'
      // A refused submit (a 409 because the iteration is already running,
      // a 403, a network error) started nothing, so it is the client's
      // failure, not the run's.
      onFailure(message, variables, 'client')
    },
  })

  /**
   * Attach to a job already running on the server. A no-op when this hook
   * is busy, when another mounted hook is already watching the job, or when
   * the job has already been seen to finish.
   */
  const resume = (jobId: string, variables: RunSchedulerParams) => {
    if (pollingRef.current || watchedJobs.has(jobId) || settledJobs.has(jobId))
      return
    watch(jobId, variables, true)
  }

  return {
    ...mutation,
    // isLoading from useMutation is only true during the initial POST.
    // Override with isJobRunning which stays true until the job completes or fails.
    isLoading: mutation.isLoading || isJobRunning,
    progress,
    weekResults,
    lastUpdateSecs,
    /**
     * The completed job's `SchedulerResult`, or null while running / after a
     * failure. Only source of `demand_adjustments` — nothing persists them.
     */
    runResult,
    resume,
  }
}

type RunSchedulerDirectOptions = {
  /**
   * The schedule this hook runs. While the schedule list says it is RUNNING,
   * the hook reports `isLoading`, whoever started the run and from wherever,
   * so every Run button for the schedule is disabled and reads Running.
   */
  schedulerId?: string
  /**
   * Also reattach to that run's progress (its `runId` in the list). Opt in
   * on the one hook per page whose progress UI should show it; the others
   * only track the status.
   */
  resume?: boolean
}

/**
 * Run scheduler hook — uses the Rust API directly (reads Azure Delta tables,
 * runs NSGA-II natively, writes output back). Returns real-time SSE progress.
 *
 * Only use this where you want the direct Rust scheduler path.
 *
 * The schedule's status in the list is written by scheduler_api itself
 * (RUNNING at the start, SUCCESS / FAILED at the end), so it is right even
 * when nobody keeps this page open. The writes here are a fallback for an
 * API that cannot reach its table, and never record a client-side failure.
 */
export const useRunSchedulerDirect = (
  onSuccess: () => void,
  onFailure: (
    errorMessage?: string,
    failures?: RunFailure[],
    origin?: JobFailureOrigin
  ) => void,
  options: RunSchedulerDirectOptions = {}
) => {
  const queryClient = useQueryClient()
  const { schedulerId: trackedSchedulerId, resume: resumes = false } = options

  // Best-effort bookkeeping write to Flask's PlanScheduler record — never
  // blocks or surfaces its own errors, so a hiccup here can't be confused
  // with the actual run's success/failure (which the caller already learns
  // about via SSE progress + onSuccess/onFailure).
  const markStatus = (params: UpdateSchedulerStatusParams) => {
    updateSchedulerStatus(params)
      .then(() => queryClient.invalidateQueries('schedules'))
      .catch((error) => {
        console.error(
          '[useRunSchedulerDirect] failed to update schedule status',
          error
        )
      })
  }

  const job = useAsyncSchedulerJob(runSchedulerDirect, {
    onSubmitted: (variables, jobId) => {
      markStatus({
        schedulerId: variables.schedulerId,
        status: 'RUNNING',
        runId: jobId,
      })
    },
    onSuccess: (variables) => {
      markStatus({ schedulerId: variables.schedulerId, status: 'SUCCESS' })
      onSuccess()
    },
    onFailure: (errorMessage, variables, origin, _nextJobId, failures) => {
      if (origin === 'server') {
        markStatus({ schedulerId: variables.schedulerId, status: 'FAILED' })
      } else {
        // The status may still change server-side; show the latest.
        queryClient.invalidateQueries('schedules')
      }
      onFailure(errorMessage, failures, origin)
    },
    timeoutMs: RUN_JOB_TIMEOUT_MS,
  })

  // The schedule list row, for the resume check. Same query key as the list
  // page, so it shares that cache.
  // The tracked schedule's status in the schedule list (same query key as the
  // list page, so it shares that cache).
  const { data: listed } = useQuery<
    { data?: { data?: SchedulerTableDataType[] } },
    unknown,
    { isRunning: boolean; runId: string | null; runStamp: string }
  >(['schedules'], () => fetchShedules(), {
    enabled: Boolean(trackedSchedulerId),
    refetchOnWindowFocus: true,
    // While it is running, keep checking, so the buttons free up when the run
    // ends even if no hook on this page is watching it.
    refetchInterval: (row) =>
      row?.isRunning ? PLAN_STATUS_FETCH_TIMEOUT : false,
    select: (raw) => {
      const row = (raw?.data?.data ?? []).find(
        (s) => String(s.id) === String(trackedSchedulerId)
      )
      const isRunning = row?.status === 'RUNNING'
      return {
        isRunning,
        runId: isRunning ? (row?.runId ?? null) : null,
        runStamp: `${row?.status ?? ''}|${row?.runId ?? ''}`,
      }
    },
  })
  const isScheduleRunning = Boolean(listed?.isRunning)

  // A run that has ended since this session last looked: refresh the
  // schedule's results (see `seenRunStamp`). Several hooks track the same
  // schedule on one page; the first to see the change records it, so the
  // refresh happens once.
  const runStamp = listed?.runStamp
  useEffect(() => {
    if (!trackedSchedulerId || runStamp === undefined) return
    const seen = seenRunStamp.get(trackedSchedulerId)
    seenRunStamp.set(trackedSchedulerId, runStamp)
    if (seen === undefined || seen === runStamp || isScheduleRunning) return
    queryClient.invalidateQueries(['scheduler', trackedSchedulerId])
    queryClient.invalidateQueries(['scheduler-settings', trackedSchedulerId])
    queryClient.invalidateQueries(['scheduler-utilisation', trackedSchedulerId])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedSchedulerId, runStamp, isScheduleRunning])
  const activeRun = listed?.runId ?? null

  const { resume, isLoading: isBusy } = job
  useEffect(() => {
    if (resumes && trackedSchedulerId && activeRun && !isBusy) {
      resume(activeRun, { schedulerId: trackedSchedulerId })
    }
    // `resume` is recreated each render; it is idempotent per job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumes, trackedSchedulerId, activeRun, isBusy])

  return {
    ...job,
    // Busy while this hook watches a job, or while the schedule list says the
    // schedule is running (started elsewhere, or watched by another hook).
    isLoading: job.isLoading || isScheduleRunning,
  }
}

/**
 * "Prepare Data" hook — submits the survey-only job and reuses the same
 * SSE/polling/timeout plumbing as Run, but deliberately does NOT touch
 * PlanScheduler's status (no `markStatus` calls at all): preparing data is
 * not a run, and the visible run-status badge must reflect Run outcomes
 * only.
 */
export const useDataPrepare = (
  onSuccess: (nextJobId?: string) => void,
  onFailure: (
    errorMessage?: string,
    nextJobId?: string,
    failures?: RunFailure[],
    origin?: JobFailureOrigin
  ) => void
) => {
  const queryClient = useQueryClient()
  return useAsyncSchedulerJob(prepareDataDirect, {
    // A Prepare the run follows (`thenRun`) is what the planner's Run click
    // started, so the badge says RUNNING at once. The API writes it too; this
    // is only so the list shows it before the API's first write lands.
    onSubmitted: (variables, jobId) => {
      if (!variables.thenRun) return
      updateSchedulerStatus({
        schedulerId: variables.schedulerId,
        status: 'RUNNING',
        runId: jobId,
      })
        .then(() => queryClient.invalidateQueries('schedules'))
        .catch((error) =>
          console.error('[useDataPrepare] failed to mark RUNNING', error)
        )
    },
    onSuccess: (_variables, _jobId, _result, nextJobId) => onSuccess(nextJobId),
    onFailure: (errorMessage, _variables, origin, nextJobId, failures) =>
      onFailure(errorMessage, nextJobId, failures, origin),
    timeoutMs: PREPARE_JOB_TIMEOUT_MS,
  })
}

// Now a post request to "/scheduler/<scheduler_id>/change-access-type" with body {access_type: 'public' or 'private'}

type ChangeAccessTypeParams = {
  schedulerId: string
  accessType: 'public' | 'private'
}

const changeAccessType = async ({
  schedulerId,
  accessType,
}: ChangeAccessTypeParams) => {
  await request({
    method: 'post',
    data: { access_type: accessType },
    url: `/scheduler/${schedulerId}/change-access-type`,
  })
}

export const useChangeAccessType = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(changeAccessType, {
    onSuccess: () => {
      onSuccess()
      queryClient.invalidateQueries('schedules')
    },
    onError: () => {
      onFailure()
    },
  })
}

/** Rename a schedule (owner only — the service refuses anyone else, 403). */
export const useRenameSchedule = (
  onSuccess: () => void,
  onFailure: (message: string) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(
    async (vars: { schedulerId: string; name: string }) =>
      request({
        method: 'put',
        url: `/scheduler/${vars.schedulerId}/rename`,
        data: { name: vars.name },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schedules')
        onSuccess()
      },
      onError: (e: any) =>
        onFailure(e?.response?.data?.error ?? 'Rename failed'),
    }
  )
}

/** Copy a schedule's latest version into a new schedule owned by the caller.
 *  `onSuccess` gets the new schedule's id and version. */
export const useCloneSchedule = (
  onSuccess: (created: { id: string; iteration: string }) => void,
  onFailure: (message: string) => void
) => {
  const queryClient = useQueryClient()
  return useMutation(
    async (vars: { schedulerId: string; name: string }) => {
      const { data } = await request({
        method: 'post',
        url: `/scheduler/${vars.schedulerId}/clone`,
        data: { name: vars.name },
      })
      return data as { id: string; iteration: string }
    },
    {
      onSuccess: (created) => {
        queryClient.invalidateQueries('schedules')
        onSuccess(created)
      },
      onError: (e: any) =>
        onFailure(e?.response?.data?.error ?? 'Clone failed'),
    }
  )
}

// Now a delete request to "/scheduler/<scheduler_id>"
type DeleteScheduleParams = {
  schedulerId: string
}

const deleteSchedule = async ({ schedulerId }: DeleteScheduleParams) => {
  await request({
    method: 'delete',
    url: `/schedulerView/${schedulerId}/0`,
  })
}

type QueryData = {
  data: {
    data: SchedulerTableDataType[]
  }
}
export const useDeleteSchedule = (
  onSuccess: () => void,
  onFailure: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(deleteSchedule, {
    onMutate: (variables: DeleteScheduleParams) => {
      queryClient.cancelQueries(['schedules'])
      const previousData = queryClient.getQueryData<QueryData>(['schedules'])
      return previousData?.data?.data?.map((item: SchedulerTableDataType) => {
        if (item.id === variables.schedulerId) {
          item.status = 'DELETING'
        }
        return item
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['schedules'])
      onSuccess()
    },
    onError: () => {
      onFailure()
    },
  })
}

// ─── Data Status (change tracking) ──────────────────────────────────────────

export type DataTableHistoryEntry = {
  timestamp: string | null
  operation: string
  version: number
}

export type DataTableStatus = {
  lastModified: string | null
  rowCount: number
  modifiedSinceRun: boolean
  history?: DataTableHistoryEntry[]
}

export type DataStatusResponse = {
  lastRunTimestamp: string | null
  status: string
  tables: Record<string, DataTableStatus>
}

const fetchDataStatus = async (schedulerId: string, iteration: string) => {
  const data = await request({
    url: `/scheduler/${schedulerId}/data-status/${iteration}`,
  })
  return data
}

/**
 * Freshness and row counts for every input table in a version.
 *
 * **This is the most expensive read in the Portal.** One call fans out into a
 * Delta metadata probe per (table, week) on the service side, so it is tuned
 * to fire on events rather than on a clock:
 *
 * - **Polling is OPT-IN and off by default.** It used to be
 *   `refetchInterval: 60000` on the hook itself, and three components mount
 *   it — the control panel, the input sidebar and the data-versions panel.
 *   In React Query v3 each observer runs its own timer, and because they
 *   mount at different moments the timers stagger, so the page issued up to
 *   three of these a minute instead of one. Exactly one caller now passes
 *   `pollMs`; the rest share its result through the cache.
 * - **Local edits do not need the poll.** Every writer already invalidates
 *   `['data-status', id, iteration]` — cell edits, CSV upload, row delete and
 *   the don't-start-before stagger — so a change made in this tab shows up
 *   immediately. The poll exists only to notice ANOTHER planner's edit, which
 *   is a far weaker requirement than once a minute.
 * - **`staleTime` is 5 minutes and focus refetching is left ON.** Those work
 *   together: `refetchOnWindowFocus` respects `staleTime`, so tabbing away and
 *   back costs nothing, while returning after a long break does re-check. The
 *   old 30s `staleTime` made almost every tab-switch a refetch.
 *   (`refetchInterval` is the one thing that ignores `staleTime` — which is
 *   why it had to be the part that went.)
 * - **`notifyOnChangeProps: 'tracked'`** keeps a background refetch from
 *   re-rendering three subscribers that only ever read `data`. 'tracked'
 *   rather than an explicit list so a consumer reading `isFetching` later
 *   still gets its re-render.
 *
 * Deliberately NOT `keepPreviousData`. It does nothing for an ordinary
 * refetch — `data` already stays populated while one is in flight, so the
 * dots never blank — and the only time it bites is a KEY change, i.e.
 * switching schedule or iteration, where it would show the previous
 * schedule's freshness against the new one's tables. Wrong data beats no data
 * on a paginated list; on a correctness indicator it is the other way round.
 */
export const useDataStatus = (
  schedulerId: string,
  iteration: string,
  /** Background poll, in ms. Leave unset unless you are the single owner. */
  pollMs: number | false = false
) => {
  return useQuery(
    ['data-status', schedulerId, iteration],
    () => fetchDataStatus(schedulerId, iteration),
    {
      select: ({ data }) => data as DataStatusResponse,
      staleTime: 5 * 60 * 1000,
      refetchInterval: pollMs,
      refetchOnWindowFocus: true,
      notifyOnChangeProps: 'tracked',
    }
  )
}

// ─── Scheduler Comments ──────────────────────────────────────────────────────

export type SchedulerComment = {
  id: string
  authorEmail: string
  authorName: string
  content: string
  productCode: string
  resourceName: string
  // Not narrowed — see SchedulerCommentAnchor.resourceType.
  resourceType: string
  startTime: number
  endTime: number
  weekNumber: number
  createdAt: string
  resolved: boolean
  /** Older rows carry no kind; the service returns 'production' for those. */
  kind?: 'production' | 'downtime'
}

const fetchComments = async (
  schedulerId: string,
  iteration: string,
  week?: number
) => {
  const params = week !== undefined ? `?week=${week}` : ''
  const data = await request({
    url: `/scheduler/${schedulerId}/${iteration}/comments${params}`,
  })
  return data
}

export const useSchedulerComments = (
  schedulerId: string,
  iteration: string,
  week?: number,
  // The Gantt views are rendered in places that don't always know a schedule
  // id, and must degrade to "no comments" rather than requesting
  // /scheduler/undefined/... — see useCommentIndex.
  enabled = true
) => {
  return useQuery(
    ['scheduler-comments', schedulerId, iteration, week],
    () => fetchComments(schedulerId, iteration, week),
    {
      enabled: enabled && Boolean(schedulerId && iteration),
      select: ({ data }) => (data?.data ?? []) as SchedulerComment[],
      refetchOnWindowFocus: true,
      staleTime: 0,
    }
  )
}

type CreateCommentParams = {
  schedulerId: string
  iteration: string
  data: {
    content: string
    productCode: string
    resourceName: string
    resourceType: string
    startTime: number
    endTime: number
    weekNumber: number
    kind?: 'production' | 'downtime'
  }
}

const createComment = async ({
  schedulerId,
  iteration,
  data,
}: CreateCommentParams) => {
  const result = await request({
    method: 'post',
    url: `/scheduler/${schedulerId}/${iteration}/comments`,
    data,
  })
  return result
}

export const useCreateComment = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(createComment, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        'scheduler-comments',
        variables.schedulerId,
        variables.iteration,
      ])
      onSuccess()
    },
    onError: () => onError(),
  })
}

type UpdateCommentParams = {
  schedulerId: string
  iteration: string
  commentId: string
  data: { content?: string; resolved?: boolean }
}

const updateComment = async ({
  schedulerId,
  iteration,
  commentId,
  data,
}: UpdateCommentParams) => {
  await request({
    method: 'put',
    url: `/scheduler/${schedulerId}/${iteration}/comments/${commentId}`,
    data,
  })
}

export const useUpdateComment = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(updateComment, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        'scheduler-comments',
        variables.schedulerId,
        variables.iteration,
      ])
      onSuccess()
    },
    onError: () => onError(),
  })
}

type DeleteCommentParams = {
  schedulerId: string
  iteration: string
  commentId: string
}

const deleteComment = async ({
  schedulerId,
  iteration,
  commentId,
}: DeleteCommentParams) => {
  await request({
    method: 'delete',
    url: `/scheduler/${schedulerId}/${iteration}/comments/${commentId}`,
  })
}

export const useDeleteComment = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation(deleteComment, {
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries([
        'scheduler-comments',
        variables.schedulerId,
        variables.iteration,
      ])
      onSuccess()
    },
    onError: () => onError(),
  })
}
